import { useEffect, useState } from 'react';
import { api, type LeisureInfo } from '../api.js';
import { TransactionEditor } from '../components/TransactionEditor.js';
import { Badge, ConceptName } from '../components/ui.js';
import { eur, periodLabel } from '../format.js';
import { useApp } from '../state.js';
import { daysInMonth, isoWeekday, makeDate } from '@balance/domain';

export function WeekendsPage() {
  const { balance, catalog, period, setPeriod, movements } = useApp();
  const [leisure, setLeisure] = useState<LeisureInfo | null>(null);
  const [newTx, setNewTx] = useState<string | null>(null);
  useEffect(() => {
    if (!period) return;
    api.leisure(period).then(setLeisure).catch(() => setLeisure(null));
  }, [period, catalog]);
  if (!balance || !catalog || !period) return <div className="skeleton" />;
  const meter = (balance.meters[period] ?? []).find((m) => catalog.versions.some((v) => v.conceptId === m.conceptId && v.frequency === 'weekend'));
  const weekendConcept = catalog.concepts.find((c) => catalog.versions.some((v) => v.conceptId === c.id && v.frequency === 'weekend'));
  const perWeekend = meter?.subPeriods.find((s) => s.budgetCents !== null)?.budgetCents ?? null;
  const { year, month } = { year: Number(period.slice(0, 4)), month: Number(period.slice(5, 7)) };
  const days = daysInMonth(year, month);
  const firstWd = isoWeekday(makeDate(year, month, 1));
  const holidays = new Map(catalog.holidays.map((h) => [h.date, h]));
  const leisureDays = new Set(leisure?.leisureDays ?? []);
  return (
    <div className="page">
      <div className="grid">
        <div className="panel">
          <div className="panelhead">
            <h2>Fines de semana · {periodLabel(period)}</h2>
            <div className="inline">
              {perWeekend !== null ? <Badge>{eur(perWeekend)} por período</Badge> : null}
              <select aria-label="Mes" value={period} onChange={(e) => setPeriod(e.target.value)} style={{ width: 'auto', margin: 0 }}>
                {balance.periods.map((p) => (
                  <option key={p.period} value={p.period}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {!weekendConcept ? <p className="muted">No hay un presupuesto por fin de semana. Crealo en Conceptos con frecuencia «por fin de semana».</p> : null}
          {meter?.subPeriods.map((s) => (
            <div className="row" key={s.key}>
              <div>
                {s.label}
                <div className="muted">{s.key === 'unassigned' ? 'Partes sin fin de semana asignado' : 'Bolsa común · sin límites por persona'}</div>
              </div>
              <div className="inline">
                <span className={`number ${s.budgetCents !== null && s.spentCents > s.budgetCents ? 'negative' : ''}`}>
                  {eur(s.spentCents)}
                  {s.budgetCents !== null ? <span className="muted"> de {eur(s.budgetCents)}</span> : null}
                </span>
                {s.key !== 'unassigned' ? (
                  <button type="button" className="button small" onClick={() => setNewTx(s.key)}>
                    Cargar gasto
                  </button>
                ) : null}
              </div>
            </div>
          ))}
          {meter ? (
            <div className="row" style={{ marginTop: 8 }}>
              <strong>
                <ConceptName id={meter.conceptId} name={meter.name} /> · total del mes
              </strong>
              <strong className={`number ${meter.overCents ? 'negative' : ''}`}>
                {eur(meter.spentCents)} <span className="muted">de {eur(meter.budgetCents)}</span>
              </strong>
            </div>
          ) : null}
          {leisure?.weekends.some((w) => w.crossesMonth) ? (
            <div className="hint" style={{ marginTop: 12 }}>
              Un período cruza de mes: cuenta en el mes de su {leisure.rule === 'start' ? 'viernes' : 'domingo'} (regla del hogar). Cada gasto admite igualmente una asignación distinta al imputarlo.
            </div>
          ) : null}
          {leisure?.floatingHolidays.length ? (
            <div className="hint" style={{ marginTop: 12 }}>
              Festivos entre semana ({leisure.floatingHolidays.map((h) => `${h.name} ${h.date}`).join(', ')}): cuentan como ocio pero el período se pregunta en cada gasto; no añaden dotación.
            </div>
          ) : null}
        </div>
        <div className="panel">
          <h2>Calendario · {periodLabel(period)}</h2>
          <p className="muted">
            Viernes desde las {catalog.household.settings.weekendStartTime} y vísperas de festivo desde las {catalog.household.settings.holidayEveStartTime}, ambas inclusive.
          </p>
          <div className="calendar">
            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
              <small key={d}>{d}</small>
            ))}
            {Array.from({ length: firstWd - 1 }, (_, i) => (
              <div key={`pad-${i}`} />
            ))}
            {Array.from({ length: days }, (_, i) => {
              const d = makeDate(year, month, i + 1);
              const h = holidays.get(d);
              const sessions = balance.rows.filter((r) => r.behavior === 'occurrence').flatMap((r) => (r.cells[period]?.occurrences ?? []).filter((o) => o.date === d).map((o) => r.name));
              return (
                <div key={d} className={`day${leisureDays.has(d) ? ' ocio' : ''}${h ? ' holiday' : ''}${d === balance.today ? ' today' : ''}`}>
                  {i + 1}
                  {h ? <small>{h.name}</small> : null}
                  {sessions.map((s) => (
                    <small key={s}>{s}</small>
                  ))}
                </div>
              );
            })}
          </div>
          <p className="muted" style={{ marginTop: 14 }}>
            Festivo en viernes o lunes se pega al fin de semana contiguo sin nueva dotación. Los festivos cargados son {catalog.holidays.some((h) => h.source === 'seed-ejemplo') ? 'de ejemplo' : 'los configurados'}; se gestionan en Configuración.
          </p>
        </div>
      </div>
      {newTx && weekendConcept ? <TransactionEditor transactionId={null} defaults={{ date: newTx.replace('weekend:', '') }} onClose={() => setNewTx(null)} /> : null}
      {movements ? null : null}
    </div>
  );
}
